import { codeFont, numFont } from '../../../utils/fonts';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LaunchIcon from '@mui/icons-material/Launch';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useUserStore } from '../../../store';
import { formatPrice, calcPriceInclVat, DEFAULT_VAT_RATE } from '../../../utils/vat';
import { formatQuantity } from '../../../utils';
import { CARD_SX, ChannelIcon, ExVatPrice, FOCUS_RING_SX, IconTile, Overline, TONE, VendorHeading, destinationText } from './builderUi';

const FIELD_SX = { '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'background.paper' } };
const BUTTON_SX = { textTransform: 'none', fontWeight: 600, boxShadow: 'none' };

/**
 * "Tarkista ja lähetä": one card per vendor order, a clear word about the split when the
 * cart spans several vendors, per-order message (and Atoy's order type fields), then
 * one request. The result view reports every order on its own: placed with its PO
 * number and destination outcomes, or failed with the reason and a retry of only the
 * failed ones.
 */
function ReviewAndSendDialog({
  open,
  onClose,
  context,
  groups,
  newLahete,
  orderOptions,
  onOrderOptionsChange,
  onSend,
  sending,
  result,
  onRetryFailed,
  onDone,
  canViewPrices,
  error,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useUserStore();

  const orderCount = groups.length;
  const setOption = (integrationId, patch) =>
    onOrderOptionsChange({ ...orderOptions, [integrationId]: { ...(orderOptions[integrationId] || {}), ...patch } });

  const dialogTitle = (title, subtitle, icon, onCloseClick, titleTestId) => (
    <DialogTitle
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5, px: { xs: 2, md: 3 }, py: { xs: 1.75, md: 2 },
        borderBottom: '1px solid', borderColor: 'divider',
      }}
    >
      <IconTile>{icon}</IconTile>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component="span"
          sx={{ display: 'block', fontSize: { xs: 17, md: 19 }, fontWeight: 700, letterSpacing: '-.3px', lineHeight: 1.2 }}
          data-testid={titleTestId}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ mt: 0.375, lineHeight: 1.4, color: 'text.secondary' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {onCloseClick && (
        <IconButton onClick={onCloseClick} aria-label={t('Close')} disabled={sending} sx={{ mt: -0.5, mr: -1, color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      )}
    </DialogTitle>
  );

  const renderLine = (line) => (
    <Box
      key={line.key}
      sx={{ display: 'flex', gap: 1.5, px: 2, py: 1.25, alignItems: 'flex-start', '& + &': { borderTop: '1px solid', borderColor: 'divider' } }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }} noWrap title={line.name}>
          {line.name}
        </Typography>
        <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: 'text.secondary', ...codeFont }}>{line.code}</Typography>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <ArrowForwardIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
          {destinationText(t, line.destination, { newLahete, inventoryEnabled: context?.inventory_tracking_enabled })}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 13, fontWeight: 500, textAlign: 'right', flexShrink: 0, whiteSpace: 'nowrap', ...numFont }}>
        {formatQuantity(line.quantity)} {line.unit || 'kpl'}
        {canViewPrices && (
          <>
            {' × '}
            <ExVatPrice value={line.purchasePrice} t={t} size={13} weight={600} />
          </>
        )}
      </Typography>
    </Box>
  );

  const renderGroupCard = (group) => {
    const integration = group.integration;
    const options = orderOptions[integration.id] || {};
    const isEmail = integration.channel === 'email';
    const total = group.totals.total;
    return (
      <Box key={integration.id} sx={{ ...CARD_SX }} data-testid={`review-order-${integration.id}`}>
        <Box sx={{ px: 2, pt: 1.75, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <VendorHeading
            integration={integration}
            t={t}
            aside={
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', ...numFont }}>{t('orderBuilder.rowCount', { count: group.lines.length })}</Typography>
            }
          />
          <Typography sx={{ fontSize: 13, color: 'text.primary', mt: 1.25, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ChannelIcon integration={integration} sx={{ fontSize: 16, color: 'text.disabled' }} />
            {isEmail
              ? t('orderBuilder.review.viaEmail', { email: integration.order_email })
              : t('orderBuilder.review.viaApi', { vendor: integration.display_name })}
          </Typography>
          {isEmail && user?.email && (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>{t('purchaseOrders.emailConfirmation', { email: user.email })}</Typography>
          )}
          {isEmail && context?.lahete_inbox_address && (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{t('purchaseOrders.emailLahete', { address: context.lahete_inbox_address })}</Typography>
          )}
        </Box>
        <Box>{group.lines.map(renderLine)}</Box>
        {canViewPrices && (
          <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.default', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
            <Box>
              <Overline>{t('orderBuilder.totalExVat')}</Overline>
              <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.5px', lineHeight: 1.15, ...numFont }}>
                {group.totals.hasUnknownPrice ? t('orderBuilder.totalPartial', { amount: formatPrice(total) }) : `${formatPrice(total)} €`}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', ...numFont }}>
              {t('orderBuilder.totalInclVat', { rate: '25,5' })} {formatPrice(calcPriceInclVat(total, DEFAULT_VAT_RATE))} €
            </Typography>
          </Box>
        )}
        <Box sx={{ px: 2, pt: 1.75, pb: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {integration.is_atoy && (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <TextField
                select
                size="small"
                label={t('purchaseOrders.atoyTilaustyyppi')}
                value={options.tilaustyyppi ?? 1}
                onChange={(e) => setOption(integration.id, { tilaustyyppi: parseInt(e.target.value, 10) })}
                sx={{ flex: '1 1 180px', ...FIELD_SX }}
              >
                <MenuItem value={1}>1 — AL Päivätilaus</MenuItem>
                <MenuItem value={2}>2 — FV Viikkotilaus</MenuItem>
                <MenuItem value={3}>3 — FK Kausitilaus</MenuItem>
                <MenuItem value={4}>4 — OS Osaset</MenuItem>
                <MenuItem value={8}>8 — MO Motonet</MenuItem>
                <MenuItem value={9}>9 — FV Jälkitoimitus sallittu</MenuItem>
              </TextField>
              <TextField
                size="small"
                label={t('purchaseOrders.atoyToimituskoodi')}
                value={options.toimituskoodi || ''}
                onChange={(e) => setOption(integration.id, { toimituskoodi: e.target.value })}
                placeholder={t('purchaseOrders.atoyToimituskoodiPlaceholder')}
                helperText={t('purchaseOrders.atoyToimituskoodiHelp')}
                sx={{ flex: '1 1 180px', ...FIELD_SX }}
              />
            </Box>
          )}
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={1}
            maxRows={4}
            label={isEmail ? t('purchaseOrders.messageToSupplier') : t('purchaseOrders.batchOrder.comment')}
            placeholder={isEmail ? t('purchaseOrders.messagePlaceholder') : t('purchaseOrders.batchOrder.commentPlaceholder')}
            value={options.comment || ''}
            onChange={(e) => setOption(integration.id, { comment: e.target.value })}
            sx={FIELD_SX}
            inputProps={{ 'data-testid': `review-comment-${integration.id}` }}
          />
        </Box>
      </Box>
    );
  };

  const renderResult = () => {
    const orders = result?.orders || [];
    const failed = orders.filter((o) => o.status === 'failed');
    const placed = orders.filter((o) => o.status === 'placed');
    const allOk = failed.length === 0;
    return (
      <>
        {dialogTitle(
          allOk ? t('orderBuilder.result.allPlaced', { count: placed.length }) : t('orderBuilder.result.partial', { placed: placed.length, failed: failed.length }),
          failed.length > 0 ? t('orderBuilder.result.failedKept') : null,
          allOk ? <CheckIcon /> : <ErrorOutlineIcon />,
          null,
          'result-summary'
        )}
        <DialogContent sx={{ px: { xs: 2, md: 3 }, py: 2.5, bgcolor: 'background.default', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {result?.created_parts_sale && (
            <Alert severity="success" sx={{ borderRadius: '12px' }} data-testid="result-new-lahete">
              {t('orderBuilder.result.newLahete', { number: result.created_parts_sale.number })}{' '}
              <Link component={RouterLink} to={`/lahetteet/${result.created_parts_sale.id}`} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
                {t('orderBuilder.open')} <LaunchIcon sx={{ fontSize: 13 }} />
              </Link>
            </Alert>
          )}
          {orders.map((order) => {
            const ok = order.status === 'placed';
            const tone = ok ? TONE.green : TONE.red;
            return (
              <Box key={order.integration_id} sx={{ ...CARD_SX, borderColor: tone.borderColor }} data-testid={`result-order-${order.integration_id}`}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5, flexWrap: 'wrap' }}>
                  <IconTile size={28} tone={tone}>
                    {ok ? <CheckIcon /> : <ErrorOutlineIcon />}
                  </IconTile>
                  <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{order.integration_name}</Typography>
                  {ok && order.order?.purchase_order_number && (
                    <Chip
                      label={order.order.purchase_order_number}
                      size="small"
                      sx={{ height: 22, fontSize: 12, fontWeight: 600, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', ...numFont }}
                    />
                  )}
                  {ok && order.order?.id && (
                    <Link
                      component={RouterLink}
                      to={`/supplier-orders?order=${order.order.id}`}
                      sx={{ fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}
                    >
                      {t('orderBuilder.open')} <LaunchIcon sx={{ fontSize: 13 }} />
                    </Link>
                  )}
                </Box>
                <Box sx={{ px: 2, pb: 1.5, mt: -0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  {ok ? (
                    <>
                      {order.order?.email_delivery?.to && (
                        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                          {t('purchaseOrders.emailOrderSuccess', { email: order.order.email_delivery.to })}
                        </Typography>
                      )}
                      {summarizeDestinations(t, order.destinations || [], context).map((text, i) => (
                        <Typography key={i} sx={{ fontSize: 13, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ArrowForwardIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                          {text}
                        </Typography>
                      ))}
                    </>
                  ) : (
                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: tone.color }} data-testid={`result-error-${order.integration_id}`}>
                      {order.error}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 1.75, gap: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          {failed.length > 0 && (
            <Button onClick={onRetryFailed} variant="outlined" sx={BUTTON_SX} data-testid="result-retry">
              {t('orderBuilder.result.retryFailed', { count: failed.length })}
            </Button>
          )}
          <Button onClick={onDone} variant="contained" sx={BUTTON_SX} data-testid="result-done">
            {t('orderBuilder.result.done')}
          </Button>
        </DialogActions>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : '16px' } }}
      sx={FOCUS_RING_SX}
      data-testid="review-dialog"
    >
      {result ? (
        renderResult()
      ) : (
        <>
          {dialogTitle(t('orderBuilder.review.title'), null, <SendOutlinedIcon />, onClose)}
          <DialogContent sx={{ px: { xs: 2, md: 3 }, py: 2.5, bgcolor: 'background.default', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {orderCount > 1 && (
              <Alert severity="info" sx={{ borderRadius: '12px', '& .MuiAlert-message': { lineHeight: 1.45 } }} data-testid="review-split-alert">
                {t('orderBuilder.review.splitIntro', { count: orderCount })}{' '}
                {groups.map((g) => `${g.integration.display_name} (${t('orderBuilder.rowCount', { count: g.lines.length })})`).join(', ')}.
              </Alert>
            )}
            {groups.map(renderGroupCard)}
            {error && (
              <Alert severity="error" sx={{ borderRadius: '12px' }} data-testid="review-error">
                {error}
              </Alert>
            )}
            {sending && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-testid="review-sending">
                <CircularProgress size={16} />
                <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{t('orderBuilder.review.sending', { count: orderCount })}</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 1.75, gap: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button onClick={onClose} disabled={sending} sx={{ textTransform: 'none', color: 'text.secondary' }}>
              {t('Cancel')}
            </Button>
            <Button
              onClick={onSend}
              variant="contained"
              disabled={sending || groups.length === 0}
              startIcon={<SendOutlinedIcon />}
              sx={BUTTON_SX}
              data-testid="review-send"
            >
              {sending ? t('orderBuilder.review.sending', { count: orderCount }) : t('orderBuilder.review.send', { count: orderCount })}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

/** "2 riviä varastoon", "Lisätty työmääräykselle #1234", "Lähete #17: 1 rivi". */
function summarizeDestinations(t, destinations, context) {
  const counts = new Map();
  destinations.forEach((d) => {
    let key;
    let label;
    if (d.kind === 'maintenance') {
      key = `m-${d.maintenance_id}`;
      label = (n) => t('orderBuilder.result.toWorkOrder', { number: d.work_number || d.maintenance_id, count: n });
    } else if (d.kind === 'parts_sale' || d.kind === 'new_parts_sale') {
      key = `s-${d.parts_sale_id}`;
      label = (n) => t('orderBuilder.result.toLahete', { number: d.parts_sale_number, count: n });
    } else {
      key = 'stock';
      label = (n) => (context?.inventory_tracking_enabled ? t('orderBuilder.result.toStock', { count: n }) : t('orderBuilder.result.noDestination', { count: n }));
    }
    const entry = counts.get(key) || { label, n: 0 };
    entry.n += 1;
    counts.set(key, entry);
  });
  return Array.from(counts.values()).map((e) => e.label(e.n));
}

export default ReviewAndSendDialog;
